import { Trash2, Sun, Droplets, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface PlantCardProps {
  id: string;
  imageUrl: string;
  popularName: string;
  scientificName: string;
  sunlightNeeds?: string;
  waterNeeds?: string;
  onDelete: (id: string) => void;
  onClick: () => void;
}

export function PlantCard({
  id,
  imageUrl,
  popularName,
  scientificName,
  sunlightNeeds,
  waterNeeds,
  onDelete,
  onClick,
}: PlantCardProps) {
  return (
    <Card className="overflow-hidden glass-card group hover:shadow-lg transition-all cursor-pointer active:scale-[0.98]">
      <div onClick={onClick}>
        <div className="relative h-36 xs:h-32 overflow-hidden">
          <img
            src={imageUrl}
            alt={popularName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
        </div>
        
        <CardContent className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground truncate">{popularName}</h3>
              <p className="text-xs text-muted-foreground italic truncate">
                {scientificName}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          </div>
          
          {(sunlightNeeds || waterNeeds) && (
            <div className="flex items-center gap-3 mt-2">
              {sunlightNeeds && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Sun className="h-3 w-3 text-sun" />
                  <span className="truncate max-w-[60px]">{sunlightNeeds}</span>
                </div>
              )}
              {waterNeeds && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Droplets className="h-3 w-3 text-water" />
                  <span className="truncate max-w-[60px]">{waterNeeds}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </div>
      
      <div className="px-3 pb-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-10 text-destructive hover:text-destructive hover:bg-destructive/10 touch-target"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remover
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remover planta?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. A planta será removida permanentemente da sua coleção.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Remover
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
}
